import { cssRaw } from 'typestyle';

/**
 * Shared styling for the list screens (Work List, Assign Work, Finished Work).
 *
 * These pages still rendered raw sha-el-design defaults while Login and the
 * Dashboard moved to the light theme, so they read as a different product.
 * This restyles them in place rather than replacing the component library --
 * that swap is a separate, much larger decision (PROJECT-ANALYSIS Phase 3).
 *
 * Scoped under `.cs-list` so nothing here can leak into the calling screens.
 * The `[class^=...]` selectors target sha-el-design's typestyle-generated
 * class prefixes; those are stable for the pinned 0.0.86 and degrade to the
 * library's own look if a future upgrade changes them -- plainer, never broken.
 */
cssRaw(`
  .cs-list {
    --cs-ink: #0F2547;
    --cs-muted: #52658A;
    --cs-line: rgba(15,37,71,0.10);
    --cs-blue: #1E6FD9;
    padding: 4px 2px 28px;
  }

  /* ---------- page header ---------- */
  .cs-list__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin: 0 0 18px;
  }
  .cs-list__title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--cs-ink);
  }
  .cs-list__subtitle {
    margin: 3px 0 0;
    font-size: 13.5px;
    color: var(--cs-muted);
  }

  /* ---------- card ----------
     NOTE: an earlier version of this rule also listed [class^='card_'].
     Measured in the browser, that selector matched ZERO elements -- sha-el-design's
     Card root does not carry a 'card_'-prefixed class. Only .cs-list__card, a
     class we own, does anything. Do not reintroduce hashed-prefix selectors:
     verify with document.querySelectorAll(...).length before trusting one. */
  .cs-list__card {
    border-radius: 14px !important;
    border: 1px solid var(--cs-line) !important;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04), 0 8px 24px rgba(15,37,71,0.06) !important;
    overflow: hidden;
  }

  /* ---------- table ---------- */
  /* font-family: sha-el's Table carries its own monospace-ish stack on the
     cells themselves; without !important the library's generated classes win. */
  .cs-list table { border-collapse: collapse; width: 100%; font-family: inherit !important; }
  .cs-list thead th, .cs-list tbody td { font-family: inherit !important; }
  .cs-list thead th {
    background: #F4F7FC !important;
    color: var(--cs-muted) !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 14px !important;
    border-bottom: 1px solid var(--cs-line) !important;
    white-space: nowrap;
  }
  .cs-list tbody td {
    padding: 12px 14px !important;
    font-size: 13.5px;
    color: var(--cs-ink);
    border-bottom: 1px solid rgba(15,37,71,0.06) !important;
    vertical-align: middle;
  }
  /* Row hover: the whole row is the affordance, not just the icon. */
  .cs-list tbody tr { transition: background-color .15s ease; }
  .cs-list tbody tr:hover { background: rgba(30,111,217,0.045); }
  .cs-list tbody tr:last-child td { border-bottom: none !important; }

  /* ---------- the call action ---------- */
  /* Was a bare <FaPhone> with an onClick -- not focusable, not keyboard
     operable, no hit area, and no hint that it was interactive. */
  .cs-list__call {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 30px; height: 30px;
    border: 1px solid rgba(30,111,217,0.30);
    border-radius: 9px;
    background: rgba(30,111,217,0.08);
    color: var(--cs-blue);
    cursor: pointer;
    padding: 0;
    transition: background-color .16s ease, border-color .16s ease, transform .16s ease;
  }
  .cs-list__call:hover {
    background: var(--cs-blue);
    border-color: var(--cs-blue);
    color: #fff;
    transform: translateY(-1px);
  }
  .cs-list__call:focus-visible {
    outline: 2px solid var(--cs-blue);
    outline-offset: 2px;
  }
  .cs-list__call[disabled] { opacity: .4; cursor: default; transform: none; }

  /* ---------- location cell ---------- */
  .cs-list__loc {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 3px 0;
  }
  .cs-list__loc-name { font-weight: 600; }
  .cs-list__count {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 7px;
    border-radius: 999px;
    background: rgba(15,37,71,0.07);
    color: var(--cs-muted);
    font-size: 11.5px;
    font-weight: 600;
  }

  /* ---------- empty state ---------- */
  .cs-list__empty {
    text-align: center;
    padding: 54px 20px;
    color: var(--cs-muted);
  }
  .cs-list__empty h4 {
    margin: 0 0 6px;
    font-size: 16px;
    color: var(--cs-ink);
  }
  .cs-list__empty p { margin: 0; font-size: 13.5px; }

  /* ---------- pagination ---------- */
  .cs-list__pager {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 4px 2px;
  }
  .cs-list__pager-info {
    margin-right: auto;
    font-size: 12.5px;
    color: var(--cs-muted);
  }
  .cs-list__page-btn {
    min-width: 34px;
    height: 32px;
    padding: 0 12px;
    border-radius: 9px;
    border: 1px solid var(--cs-line);
    background: #fff;
    color: var(--cs-ink);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color .16s ease, border-color .16s ease, color .16s ease;
  }
  .cs-list__page-btn:hover:not([disabled]) {
    border-color: rgba(30,111,217,0.45);
    background: rgba(30,111,217,0.06);
    color: var(--cs-blue);
  }
  .cs-list__page-btn:focus-visible { outline: 2px solid var(--cs-blue); outline-offset: 2px; }
  .cs-list__page-btn[disabled] { opacity: .45; cursor: default; }
  .cs-list__page-current {
    min-width: 34px; height: 32px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 9px;
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%);
    color: #fff; font-size: 13px; font-weight: 700;
  }

  /* ---------- page header actions ----------
     sha-el-design's primary Button is flat navy; align it with the gradient
     used on the login submit and the home hero so the pages read as one app.

     Targets the real rendered <button> under our own wrapper class rather than
     the library's typestyle hash -- an earlier attempt used
     [class^='page_'] [class^='button_'] and silently matched nothing. */
  .cs-list__action button,
  .cs-list__action a {
    width: 100%;
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%) !important;
    border: none !important;
    border-radius: 10px !important;
    font-weight: 600 !important;
    letter-spacing: .01em;
    box-shadow: 0 4px 14px rgba(30,64,175,0.22);
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
  }
  .cs-list__action button:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(30,64,175,0.30);
    filter: brightness(1.06);
  }
  .cs-list__action button:focus-visible {
    outline: 2px solid #1E6FD9;
    outline-offset: 2px;
  }

  /* ---------- filter bar ----------
     The search box was a floated div with a hardcoded 300px width, which
     collided with the table on narrow viewports. */
  .cs-list__filters {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    padding: 4px 4px 14px;
  }
  .cs-list__filter { flex: 0 1 300px; min-width: 200px; }
  @media (max-width: 720px) {
    .cs-list__filter { flex: 1 1 100%; }
  }

  @media (max-width: 720px) {
    .cs-list thead th, .cs-list tbody td { padding: 10px !important; font-size: 12.5px; }
    .cs-list__title { font-size: 19px; }
  }

  /* =====================================================================
     Allocation cards -- replaces the 10-column table on the three work lists.
     A table forced every field to equal weight and pushed the numbers that
     matter (how far along is this? is it late?) into narrow columns. A card
     can lead with the caller, show progress as a bar, and colour the deadline
     by urgency.
     ===================================================================== */
  .cs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(430px, 1fr));
    gap: 14px;
    padding: 2px;
  }
  @media (max-width: 560px) { .cs-grid { grid-template-columns: 1fr; } }

  .cs-alloc {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 18px 14px;
    background: #fff;
    border: 1px solid rgba(15,37,71,0.10);
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04);
    transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease;
  }
  .cs-alloc:hover {
    transform: translateY(-2px);
    border-color: rgba(30,111,217,0.30);
    box-shadow: 0 10px 26px rgba(15,37,71,0.10);
  }
  /* Accent rail keyed to status, so a wall of cards is scannable by colour. */
  .cs-alloc::before {
    content: '';
    position: absolute;
    left: 0; top: 14px; bottom: 14px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: rgba(15,37,71,0.14);
  }
  .cs-alloc--doing::before    { background: linear-gradient(180deg,#1E6FD9,#0A6BA8); }
  .cs-alloc--finished::before { background: #16A34A; }
  .cs-alloc--onhold::before   { background: #F59E0B; }
  .cs-alloc--overdue::before  { background: #DC2626; }

  .cs-alloc__head { display: flex; align-items: flex-start; gap: 11px; }
  .cs-alloc__avatar {
    flex: 0 0 38px; width: 38px; height: 38px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 11px;
    background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%);
    color: #fff; font-size: 13.5px; font-weight: 700; letter-spacing: .02em;
  }
  .cs-alloc__ident { min-width: 0; flex: 1 1 auto; }
  .cs-alloc__name {
    margin: 0; font-size: 15px; font-weight: 700; color: #0F2547;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cs-alloc__where {
    margin: 2px 0 0; font-size: 12.5px; color: #52658A;
    display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  }
  .cs-alloc__status {
    flex: 0 0 auto;
    padding: 3px 10px; border-radius: 999px;
    font-size: 10.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
    background: rgba(15,37,71,0.07); color: #52658A;
  }
  .cs-alloc__status--doing    { background: rgba(30,111,217,0.12); color: #1653A8; }
  .cs-alloc__status--finished { background: rgba(22,163,74,0.13);  color: #15803D; }
  .cs-alloc__status--onhold   { background: rgba(245,158,11,0.16); color: #B45309; }
  .cs-alloc__status--pending  { background: rgba(15,37,71,0.07);   color: #52658A; }

  /* ---------- progress ---------- */
  .cs-alloc__progress { display: flex; flex-direction: column; gap: 5px; }
  .cs-alloc__bar {
    position: relative; height: 7px; border-radius: 99px;
    background: rgba(15,37,71,0.08); overflow: hidden;
  }
  .cs-alloc__bar span {
    display: block; height: 100%; border-radius: 99px;
    background: linear-gradient(90deg,#38BDF8 0%,#1E6FD9 100%);
    transition: width .45s cubic-bezier(.4,0,.2,1);
  }
  .cs-alloc__bar--done span { background: linear-gradient(90deg,#4ADE80 0%,#16A34A 100%); }
  .cs-alloc__figures {
    display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
    font-size: 12px; color: #52658A;
  }
  .cs-alloc__figures b { color: #0F2547; font-size: 13px; }

  /* ---------- footer ---------- */
  .cs-alloc__foot {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap;
    padding-top: 11px;
    border-top: 1px solid rgba(15,37,71,0.07);
  }
  .cs-alloc__dates { display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: #52658A; }
  .cs-alloc__due { font-weight: 600; }
  .cs-alloc__due--soon    { color: #B45309; }
  .cs-alloc__due--overdue { color: #DC2626; }
  .cs-alloc__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

  /* ---------- location rows (Work List: one call button per town/area) ---------- */
  .cs-alloc__locs { display: flex; flex-direction: column; gap: 6px; }
  .cs-alloc__loc {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 8px 10px;
    background: #F7F9FD;
    border: 1px solid rgba(15,37,71,0.07);
    border-radius: 10px;
    transition: background-color .16s ease, border-color .16s ease;
  }
  .cs-alloc__loc:hover { background: rgba(30,111,217,0.06); border-color: rgba(30,111,217,0.22); }
  .cs-alloc__loc-name { font-size: 13px; font-weight: 600; color: #0F2547; }
  .cs-alloc__loc-sub { font-size: 11.5px; color: #52658A; }

  .cs-alloc__go {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 14px;
    border: none; border-radius: 9px;
    background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%);
    color: #fff; font-size: 12.5px; font-weight: 700;
    cursor: pointer; white-space: nowrap;
    box-shadow: 0 3px 10px rgba(30,64,175,0.24);
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
  }
  .cs-alloc__go:hover { transform: translateY(-1px); filter: brightness(1.07);
                        box-shadow: 0 7px 18px rgba(30,64,175,0.32); }
  .cs-alloc__go:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }

  /* ---------- reassignment strip (Assigned list only) ---------- */
  .cs-alloc__reassign {
    display: flex; align-items: flex-end; gap: 9px;
    padding-top: 10px; margin-top: 2px;
    border-top: 1px dashed rgba(15,37,71,0.12);
  }
  .cs-alloc__reassign-field { flex: 1 1 auto; min-width: 0; }
  .cs-alloc__reassign button { border-radius: 9px !important; }

  /* =====================================================================
     People cards -- the user directory
     ===================================================================== */
  .cs-people {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
    padding: 2px;
  }
  @media (max-width: 520px) { .cs-people { grid-template-columns: 1fr; } }

  .cs-person {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid rgba(15,37,71,0.10);
    border-radius: 13px;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04);
    text-decoration: none;
    transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  }
  .cs-person:hover {
    transform: translateY(-2px);
    border-color: rgba(30,111,217,0.32);
    box-shadow: 0 10px 24px rgba(15,37,71,0.10);
  }
  .cs-person:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }
  .cs-person__avatar {
    flex: 0 0 42px; width: 42px; height: 42px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 12px;
    color: #fff; font-size: 14px; font-weight: 700; letter-spacing: .02em;
  }
  /* Role is carried by the avatar tint AND a text badge -- not colour alone. */
  .cs-person__avatar--admin      { background: linear-gradient(135deg,#7C3AED 0%,#5B21B6 100%); }
  .cs-person__avatar--supervisor { background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%); }
  .cs-person__avatar--caller     { background: linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%); }
  .cs-person__body { min-width: 0; flex: 1 1 auto; }
  .cs-person__name {
    margin: 0; font-size: 14.5px; font-weight: 700; color: #0F2547;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cs-person__handle {
    margin: 1px 0 0; font-size: 12px; color: #52658A;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cs-person__reports { margin: 5px 0 0; font-size: 11.5px; color: #7386A6; }
  .cs-person__role {
    flex: 0 0 auto;
    padding: 3px 9px; border-radius: 999px;
    font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase;
  }
  .cs-person__role--admin      { background: rgba(124,58,237,0.13); color: #6D28D9; }
  .cs-person__role--supervisor { background: rgba(30,111,217,0.13); color: #1653A8; }
  .cs-person__role--caller     { background: rgba(14,165,233,0.15); color: #0369A1; }

  /* =====================================================================
     Assign Work wizard
     ===================================================================== */
  .cs-wiz { --cs-ink: #0F2547; --cs-muted: #52658A; --cs-blue: #1E6FD9;
            --cs-line: rgba(15,37,71,0.10); }

  /* ---------- step indicator ----------
     The wizard previously gave no indication of progress at all: the card
     title read "Select State and Employee" on every step, so there was no
     way to tell step 1 from step 3. */
  .cs-wiz__steps {
    display: flex;
    align-items: center;
    gap: 0;
    margin: 0 0 18px;
    flex-wrap: wrap;
  }
  .cs-wiz__step {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 0 4px;
  }
  .cs-wiz__dot {
    width: 26px; height: 26px;
    flex: 0 0 26px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 50%;
    font-size: 12px; font-weight: 700;
    background: rgba(15,37,71,0.07);
    color: var(--cs-muted);
    border: 1px solid transparent;
    transition: background-color .2s ease, color .2s ease, border-color .2s ease;
  }
  .cs-wiz__label {
    font-size: 13px; font-weight: 600; color: var(--cs-muted);
    white-space: nowrap;
    transition: color .2s ease;
  }
  .cs-wiz__step--active .cs-wiz__dot {
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%);
    color: #fff;
    box-shadow: 0 3px 10px rgba(30,64,175,0.30);
  }
  .cs-wiz__step--active .cs-wiz__label { color: var(--cs-ink); }
  .cs-wiz__step--done .cs-wiz__dot {
    background: rgba(30,111,217,0.13);
    color: var(--cs-blue);
    border-color: rgba(30,111,217,0.35);
  }
  .cs-wiz__step--done .cs-wiz__label { color: var(--cs-blue); }
  .cs-wiz__bar {
    flex: 1 1 26px;
    min-width: 18px;
    height: 2px;
    background: rgba(15,37,71,0.10);
    margin: 0 10px;
    border-radius: 2px;
  }
  .cs-wiz__bar--done { background: rgba(30,111,217,0.45); }

  /* ---------- wizard body ---------- */
  .cs-wiz__hint {
    margin: -6px 0 16px;
    font-size: 13px;
    color: var(--cs-muted);
  }
  /* Style the real rendered element under a class we own -- measured: the
     hashed [class^='card_'] selector this replaced matched 0 elements. */
  .cs-wiz__card > * {
    border-radius: 14px !important;
    border: 1px solid var(--cs-line) !important;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04), 0 8px 24px rgba(15,37,71,0.06) !important;
    overflow: hidden;
  }

  /* Wizard footer buttons: match the brand gradient used everywhere else. */
  .cs-wiz__actions button {
    border-radius: 10px !important;
    font-weight: 600 !important;
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
  }
  .cs-wiz__actions button:last-child {
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%) !important;
    border: none !important;
    box-shadow: 0 4px 14px rgba(30,64,175,0.22);
  }
  .cs-wiz__actions button:hover:not([disabled]) {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }
  .cs-wiz__actions button:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }

  /* Selected town: one town may be chosen from a long scrolling list, so the
     whole row needs to show it, not just a 16px tick. */
  .cs-wiz__town--selected {
    background: rgba(30,111,217,0.07);
    box-shadow: inset 3px 0 0 #1E6FD9;
    border-radius: 8px;
    padding-left: 9px !important;
    margin-left: -9px;
  }
  .cs-wiz__town-check { display: inline-flex; align-items: center; }
  /* Areas nested under an expanded town, indented and separated so they are
     not mistaken for further town rows. */
  .cs-wiz__area {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0 7px 22px;
    border-top: 1px solid rgba(15,37,71,0.06);
  }
  .cs-wiz__area:first-child { border-top: none; }

  /* ---------- town picker rows ---------- */
  .cs-wiz__townbar {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 4px;
  }
  .cs-wiz__townbar-search { flex: 1 1 260px; min-width: 200px; }
  .cs-wiz__town {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    width: 100%;
    padding: 2px 0;
  }
  .cs-wiz__town-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .cs-wiz__pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    background: rgba(15,37,71,0.06);
    color: var(--cs-muted);
    white-space: nowrap;
  }
  .cs-wiz__pill--count {
    background: rgba(30,111,217,0.10);
    color: var(--cs-blue);
  }
  .cs-wiz__pill--empty { background: rgba(15,37,71,0.05); color: #8A97AD; }

  /* ---------- review panel ---------- */
  /* Was a stack of <h4>s -- including <ol> lists nested INSIDE headings,
     which is invalid markup and rendered list items at heading weight. */
  .cs-wiz__review-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 0;
    border-bottom: 1px solid rgba(15,37,71,0.06);
  }
  .cs-wiz__review-row:last-child { border-bottom: none; }
  .cs-wiz__review-label {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--cs-muted);
    white-space: nowrap;
  }
  .cs-wiz__review-value {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--cs-ink);
    text-align: right;
  }
  .cs-wiz__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    justify-content: flex-end;
  }
  .cs-wiz__chip {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 7px;
    background: rgba(30,111,217,0.10);
    color: var(--cs-blue);
    font-size: 12px;
    font-weight: 600;
  }
  .cs-wiz__review-empty { color: #8A97AD; font-weight: 500; font-style: italic; }

  @media (max-width: 900px) {
    .cs-wiz__label { display: none; }
    .cs-wiz__bar { margin: 0 6px; }
    .cs-wiz__town { align-items: flex-start; flex-direction: column; gap: 6px; }
  }

  /* =====================================================================
     Form pages -- Add / Edit User Detail.
     Was a flat 2x4 grid of unrelated fields: territory first, credentials
     in the middle, and the role -- the field that decides what the whole
     account can do -- second to last, in a dropdown labelled 'Uesr Type'.
     Group the fields by concern and make the role an explicit choice.
     ===================================================================== */
  .cs-form { --cs-ink: #0F2547; --cs-muted: #52658A; --cs-blue: #1E6FD9;
             --cs-line: rgba(15,37,71,0.10); }

  /* Style the real rendered element under a class we own -- same reasoning
     as .cs-wiz__card: hashed-prefix selectors matched nothing. */
  .cs-form__card > * {
    border-radius: 14px !important;
    border: 1px solid var(--cs-line) !important;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04), 0 8px 24px rgba(15,37,71,0.06) !important;
    overflow: hidden;
  }

  .cs-form__section { padding: 4px 0 14px; }
  .cs-form__section + .cs-form__section {
    border-top: 1px solid rgba(15,37,71,0.07);
    padding-top: 18px;
  }
  .cs-form__legend {
    margin: 0;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--cs-muted);
  }
  .cs-form__hint { margin: 3px 0 12px; font-size: 13px; color: #7386A6; }

  .cs-form__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 18px;
    row-gap: 4px;
    align-items: start;
  }
  .cs-form__grid--3 { grid-template-columns: 1fr 1fr 1fr; }
  .cs-form__wide { grid-column: 1 / -1; }
  @media (max-width: 760px) {
    .cs-form__grid, .cs-form__grid--3 { grid-template-columns: 1fr; }
  }

  /* ---------- role picker ---------- */
  .cs-form__roles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin: 2px 0 6px;
  }
  @media (max-width: 760px) { .cs-form__roles { grid-template-columns: 1fr; } }
  .cs-form__role {
    text-align: left;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid var(--cs-line);
    border-radius: 12px;
    cursor: pointer;
    transition: border-color .16s ease, background-color .16s ease,
                box-shadow .16s ease, transform .16s ease;
  }
  .cs-form__role:hover {
    transform: translateY(-1px);
    border-color: rgba(30,111,217,0.35);
    box-shadow: 0 6px 16px rgba(15,37,71,0.08);
  }
  .cs-form__role:focus-visible { outline: 2px solid var(--cs-blue); outline-offset: 2px; }
  .cs-form__role-name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .05em;
    color: var(--cs-ink);
  }
  .cs-form__role-blurb {
    display: block;
    margin-top: 3px;
    font-size: 12px;
    line-height: 1.45;
    color: var(--cs-muted);
  }
  /* Generic selected state, for pickers whose choices have no colour identity
     of their own (e.g. the question-type cards). Role-specific rules below win
     on specificity. */
  .cs-form__role--selected { border-color: #1E6FD9; background: rgba(30,111,217,0.06); }
  .cs-form__role--selected .cs-form__role-name { color: #1653A8; }
  /* Selected: the same colour identities as the people directory, so the
     role this card creates is the colour the directory shows it in. */
  .cs-form__role--selected.cs-form__role--admin      { border-color: #7C3AED; background: rgba(124,58,237,0.08); }
  .cs-form__role--selected.cs-form__role--admin      .cs-form__role-name { color: #6D28D9; }
  .cs-form__role--selected.cs-form__role--supervisor { border-color: #1E6FD9; background: rgba(30,111,217,0.08); }
  .cs-form__role--selected.cs-form__role--supervisor .cs-form__role-name { color: #1653A8; }
  .cs-form__role--selected.cs-form__role--caller     { border-color: #0EA5E9; background: rgba(14,165,233,0.10); }
  .cs-form__role--selected.cs-form__role--caller     .cs-form__role-name { color: #0369A1; }

  .cs-form__supervisor { max-width: 420px; }

  /* ---------- footer actions ----------
     Clear and Submit were twin full-width primary buttons: equal visual
     weight for 'wipe the form' and 'save'. One quiet, one branded. */
  .cs-form__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 16px;
    margin-top: 6px;
    border-top: 1px solid rgba(15,37,71,0.07);
  }
  .cs-form__actions button {
    min-width: 150px;
    border-radius: 10px !important;
    font-weight: 600 !important;
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
  }
  /* The quiet actions: sha-el's 'secondary' Button is pink, which made Clear
     read as the dangerous one. Neutral ghosts for everything but the primary
     (the questionnaire form has three: Duplicate / Reset / Save). */
  .cs-form__actions button:not(:last-child) {
    background: #fff !important;
    border: 1px solid var(--cs-line) !important;
    color: var(--cs-muted) !important;
    box-shadow: none !important;
  }
  .cs-form__actions button:not(:last-child):hover {
    border-color: rgba(30,111,217,0.40) !important;
    color: var(--cs-blue) !important;
    background: rgba(30,111,217,0.05) !important;
  }
  .cs-form__actions button:last-child {
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%) !important;
    border: none !important;
    box-shadow: 0 4px 14px rgba(30,64,175,0.22);
  }
  .cs-form__actions button:hover:not([disabled]) {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }
  .cs-form__actions button:focus-visible { outline: 2px solid var(--cs-blue); outline-offset: 2px; }
  .cs-form__actions button[disabled] { opacity: .55; cursor: default; }
  @media (max-width: 560px) {
    .cs-form__actions { flex-direction: column-reverse; align-items: stretch; }
    .cs-form__actions button { width: 100%; }
  }

  /* ---------- read-only banner (question referenced by questionnaires) ----
     The old form 'enforced' this by silently ignoring keystrokes: the inputs
     looked editable and the Submit button stayed live. Say it out loud. */
  .cs-form__lock {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin: 2px 0 16px;
    padding: 11px 14px;
    background: rgba(245,158,11,0.10);
    border: 1px solid rgba(245,158,11,0.35);
    border-radius: 10px;
    font-size: 13px;
    color: #7C4A03;
  }
  .cs-form__lock b { color: #92400E; }
  /* sha-el barely distinguishes disabled inputs; make the locked state visible. */
  .cs-form input[disabled] {
    background: #F4F7FC !important;
    color: #52658A !important;
    cursor: not-allowed;
  }

  /* ---------- options editor ---------- */
  .cs-form__opts { max-width: 560px; }
  .cs-form__opt {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 2px 0;
  }
  .cs-form__opt-field { flex: 1 1 auto; min-width: 0; }
  .cs-form__opt-letter {
    flex: 0 0 26px;
    width: 26px; height: 26px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 50%;
    background: rgba(15,37,71,0.07);
    color: var(--cs-muted);
    font-size: 12px; font-weight: 700;
  }
  .cs-form__opt-remove {
    flex: 0 0 30px;
    width: 30px; height: 30px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1px solid var(--cs-line);
    border-radius: 9px;
    background: #fff;
    color: var(--cs-muted);
    font-size: 16px;
    cursor: pointer;
    transition: border-color .16s ease, color .16s ease, background-color .16s ease;
  }
  .cs-form__opt-remove:hover:not([disabled]) {
    border-color: rgba(220,38,38,0.45);
    color: #DC2626;
    background: rgba(220,38,38,0.06);
  }
  .cs-form__opt-remove:focus-visible { outline: 2px solid var(--cs-blue); outline-offset: 2px; }
  .cs-form__opt-remove[disabled] { opacity: .35; cursor: default; }
  .cs-form__add-opt {
    margin: 8px 0 0 36px;
    padding: 7px 14px;
    border: 1px dashed rgba(30,111,217,0.45);
    border-radius: 9px;
    background: rgba(30,111,217,0.04);
    color: var(--cs-blue);
    font-size: 12.5px; font-weight: 600;
    cursor: pointer;
    transition: background-color .16s ease, border-color .16s ease;
  }
  .cs-form__add-opt:hover { background: rgba(30,111,217,0.10); border-color: var(--cs-blue); }
  .cs-form__add-opt:focus-visible { outline: 2px solid var(--cs-blue); outline-offset: 2px; }
  .cs-form__note {
    margin: 10px 0 0;
    font-size: 12px;
    color: #7386A6;
  }

  /* ---------- question list ---------- */
  .cs-q__name {
    font-weight: 600;
    color: #0F2547;
    text-decoration: none;
  }
  .cs-q__name:hover { color: #1E6FD9; }
  .cs-q__desc { margin: 2px 0 0; font-size: 12px; color: #52658A; }
  .cs-q__type {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 10.5px; font-weight: 700;
    letter-spacing: .05em; text-transform: uppercase;
    white-space: nowrap;
  }
  .cs-q__type--multiple { background: rgba(30,111,217,0.12);  color: #1653A8; }
  .cs-q__type--single   { background: rgba(13,148,136,0.13);  color: #0F766E; }
  .cs-q__type--text     { background: rgba(15,37,71,0.08);    color: #52658A; }
  .cs-q__status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px; font-weight: 700;
    white-space: nowrap;
  }
  .cs-q__status--locked { background: rgba(245,158,11,0.15); color: #B45309; }
  .cs-q__status--free   { background: rgba(22,163,74,0.12);  color: #15803D; }
  .cs-q__status--off    { background: rgba(15,37,71,0.08);   color: #52658A; }

  /* Row action: the Activate / Deactivate toggle on the questionnaire list.
     Quiet by default; the destructive direction only turns red on intent. */
  .cs-q__toggle {
    padding: 6px 12px;
    border: 1px solid var(--cs-line);
    border-radius: 9px;
    background: #fff;
    color: var(--cs-muted);
    font-size: 12px; font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color .16s ease, color .16s ease, background-color .16s ease;
  }
  .cs-q__toggle--off:hover:not([disabled]) {
    border-color: rgba(220,38,38,0.45); color: #DC2626; background: rgba(220,38,38,0.05);
  }
  .cs-q__toggle--on:hover:not([disabled]) {
    border-color: rgba(22,163,74,0.50); color: #15803D; background: rgba(22,163,74,0.06);
  }
  .cs-q__toggle:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }
  .cs-q__toggle[disabled] { opacity: .45; cursor: default; }

  /* ---------- call-script textareas (questionnaire form) ----------
     Were raw unstyled <textarea>s beside a bare <span> label. */
  .cs-form__field-label {
    display: block;
    margin: 0 0 6px;
    font-size: 12px; font-weight: 600;
    color: var(--cs-muted);
  }
  .cs-form__textarea {
    display: block;
    width: 100%;
    min-height: 96px;
    padding: 10px 12px;
    border: 1px solid var(--cs-line);
    border-radius: 10px;
    background: #fff;
    color: var(--cs-ink);
    font: inherit;
    font-size: 13.5px;
    line-height: 1.5;
    resize: vertical;
    transition: border-color .16s ease, box-shadow .16s ease;
  }
  .cs-form__textarea:focus {
    outline: none;
    border-color: #1E6FD9;
    box-shadow: 0 0 0 3px rgba(30,111,217,0.12);
  }
  .cs-form__textarea::placeholder { color: #9AA8BF; }

  /* =====================================================================
     Reports -- Call History, per-caller detail, Retailer report, Q&A.
     These four screens were the last on raw sha-el defaults: bare Cards with
     no page chrome on three of them, filters in a 3-up Row with no alignment,
     numbers rendered as plain text in a wall of identical columns.
     ===================================================================== */

  /* Filter bar: the report filters are the primary control, not an
     afterthought -- give them a surface of their own above the table.

     Each control MUST be wrapped in .cs-rep__field. sha-el's Input renders
     TWO siblings -- a container and an always-present (usually empty) help
     div -- so dropping Inputs straight into the grid gave six tracks for four
     controls, with the empty help divs eating whole columns. Measured before
     this fix: the two date boxes sat at y=296, the caller at y=298 and the
     button at y=320, because the container also carries a 24px bottom margin
     that align-items:end then measured from.
     (No backticks in this file -- the whole stylesheet is a JS template
     literal, so one would end the string.) */
  /* Widths follow content: a date is a fixed-width thing, a person's name is
     not, so the dates are capped and the caller field takes the slack rather
     than all three stretching to a third of the page each. */
  .cs-rep__filters {
    display: grid;
    grid-template-columns:
      minmax(140px, 190px)
      minmax(140px, 190px)
      minmax(170px, 260px)
      auto;
    gap: 12px 14px;
    align-items: end;
    /* Pack to the left so the four controls read as one group, with the
       button beside the fields it applies to rather than stranded across
       the page from them. */
    justify-content: start;
    padding: 4px 2px 18px;
  }
  /* The retailer report has seven controls. Left to auto-fit they wrapped
     five-then-two, stranding Search beside To Date. Lay them out on purpose:
     State / Town / Area / Retailer, then From / To with Search under
     Retailer in the fourth column. */
  .cs-rep__filters--wide {
    grid-template-columns: repeat(4, minmax(150px, 1fr));
  }
  .cs-rep__filters--wide .cs-rep__search { grid-column: 4; width: 100%; }

  .cs-rep__field { min-width: 0; }
  /* Flatten the library's spacing inside our own wrapper only. */
  .cs-rep__field div { margin-bottom: 0 !important; }
  .cs-rep__field div:empty { display: none; }
  /* One height for every control on the row, button included, so the boxes
     line up on both edges rather than merely sharing a baseline. */
  .cs-rep__filters input {
    height: 42px !important;
    box-sizing: border-box !important;
  }
  /* The library's Input container adds 4px of its own padding below the
     visible box, so a bare grid child aligned to end lands 4px lower than
     the inputs it sits beside. Compensate, and keep the harness assertion
     that all four boxes share a top and a bottom edge -- if a future
     sha-el bump changes that padding, the check fails rather than the
     alignment silently drifting. */
  .cs-rep__filters .cs-rep__search { height: 42px; margin-bottom: 4px; min-width: 130px; }

  /* The --wide variant needs naming in these too: two class names outrank one,
     so a bare .cs-rep__filters rule would not override it. */
  @media (max-width: 900px) {
    .cs-rep__filters,
    .cs-rep__filters--wide { grid-template-columns: repeat(2, minmax(150px, 1fr)); }
    .cs-rep__filters .cs-rep__search,
    .cs-rep__filters--wide .cs-rep__search { grid-column: 1 / -1; width: 100%; }
  }
  @media (max-width: 560px) {
    .cs-rep__filters,
    .cs-rep__filters--wide { grid-template-columns: 1fr; }
  }

  /* Search button: reports now apply their filters on demand rather than on
     every keystroke -- a wide date range is a real query, not a free one. */
  .cs-rep__search {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 40px;
    padding: 0 22px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%);
    color: #fff;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: .01em;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 4px 14px rgba(30,64,175,0.22);
    transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
  }
  .cs-rep__search:hover:not([disabled]) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow: 0 8px 20px rgba(30,64,175,0.30);
  }
  .cs-rep__search:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }
  .cs-rep__search[disabled] { opacity: .5; cursor: default; box-shadow: none; }
  /* Unapplied edits: say so, rather than showing numbers that no longer match
     the boxes above them. */
  .cs-rep__dirty {
    padding: 2px 9px;
    border-radius: 999px;
    background: rgba(30,111,217,0.12);
    color: #1653A8;
    font-size: 11.5px;
    font-weight: 700;
  }

  /* The range summary line: says in words what the numbers below cover. */
  .cs-rep__scope {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    margin: -6px 0 14px;
    font-size: 12.5px;
    color: var(--cs-muted, #52658A);
  }
  .cs-rep__scope b { color: #0F2547; font-weight: 600; }
  .cs-rep__warn {
    padding: 2px 9px;
    border-radius: 999px;
    background: rgba(245,158,11,0.15);
    color: #B45309;
    font-size: 11.5px;
    font-weight: 700;
  }

  /* Numeric cells: tabular figures so columns of counts line up. */
  .cs-rep__num {
    display: inline-block;
    min-width: 2ch;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #0F2547;
  }
  .cs-rep__num--zero { color: #9AA8BF; font-weight: 500; }
  .cs-rep__num--alert { color: #B45309; }

  /* Call outcome pills, same vocabulary as the calling screen. */
  .cs-rep__status {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 10.5px; font-weight: 700;
    letter-spacing: .04em; text-transform: uppercase;
    white-space: nowrap;
  }
  .cs-rep__status--answered { background: rgba(22,163,74,0.13);  color: #15803D; }
  .cs-rep__status--noreply  { background: rgba(245,158,11,0.16); color: #B45309; }
  .cs-rep__status--wrong    { background: rgba(220,38,38,0.12);  color: #B91C1C; }
  .cs-rep__status--other    { background: rgba(15,37,71,0.07);   color: #52658A; }

  .cs-rep__muted { color: #7386A6; font-size: 12.5px; }

  /* ---------- date-range presets ----------
     Picking "the last month" should not mean operating two date pickers. */
  .cs-rep__presets {
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
    padding: 2px 2px 14px;
  }
  .cs-rep__presets-label {
    font-size: 11.5px; font-weight: 700; letter-spacing: .06em;
    text-transform: uppercase; color: var(--cs-muted, #52658A);
    margin-right: 3px;
  }
  .cs-chip {
    padding: 6px 13px;
    border: 1px solid var(--cs-line, rgba(15,37,71,0.10));
    border-radius: 999px;
    background: #fff;
    color: #52658A;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color .15s ease, color .15s ease, background-color .15s ease;
  }
  .cs-chip:hover {
    border-color: rgba(30,111,217,0.45);
    color: #1E6FD9;
    background: rgba(30,111,217,0.05);
  }
  .cs-chip:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }
  .cs-chip--on {
    border-color: transparent;
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%);
    color: #fff;
    box-shadow: 0 3px 10px rgba(30,64,175,0.24);
  }
  .cs-chip--on:hover { color: #fff; background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%); }

  /* ---------- headline tiles ----------
     The first question a supervisor asks is "how did the team do?", which the
     old table made you answer by adding up a column in your head. */
  .cs-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 12px;
    margin: 2px 0 18px;
  }
  .cs-stat {
    position: relative;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid rgba(15,37,71,0.10);
    border-radius: 13px;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04);
    overflow: hidden;
  }
  .cs-stat::before {
    content: '';
    position: absolute; left: 0; top: 12px; bottom: 12px;
    width: 3px; border-radius: 0 3px 3px 0;
    background: rgba(15,37,71,0.14);
  }
  .cs-stat--calls::before    { background: linear-gradient(180deg,#38BDF8,#1E6FD9); }
  .cs-stat--answered::before { background: linear-gradient(180deg,#4ADE80,#16A34A); }
  .cs-stat--rate::before     { background: linear-gradient(180deg,#A78BFA,#7C3AED); }
  .cs-stat--alert::before    { background: linear-gradient(180deg,#FCD34D,#B45309); }
  .cs-stat__label {
    display: block;
    font-size: 10.5px; font-weight: 700;
    letter-spacing: .07em; text-transform: uppercase;
    color: #52658A;
  }
  .cs-stat__value {
    display: block; margin-top: 4px;
    font-size: 25px; font-weight: 700; line-height: 1.1;
    color: #0F2547;
    font-variant-numeric: tabular-nums;
  }
  .cs-stat__sub { display: block; margin-top: 3px; font-size: 11.5px; color: #7386A6; }

  /* ---------- connect-rate meter ---------- */
  .cs-rate { display: flex; align-items: center; gap: 9px; min-width: 118px; }
  .cs-rate__track {
    position: relative; flex: 1 1 auto; height: 6px;
    border-radius: 99px; background: rgba(15,37,71,0.08); overflow: hidden;
  }
  .cs-rate__fill {
    display: block; height: 100%; border-radius: 99px;
    background: linear-gradient(90deg,#4ADE80 0%,#16A34A 100%);
  }
  .cs-rate__fill--mid { background: linear-gradient(90deg,#FCD34D 0%,#F59E0B 100%); }
  .cs-rate__fill--low { background: linear-gradient(90deg,#FCA5A5 0%,#DC2626 100%); }
  .cs-rate__pct {
    flex: 0 0 auto; min-width: 38px; text-align: right;
    font-size: 12.5px; font-weight: 700; color: #0F2547;
    font-variant-numeric: tabular-nums;
  }

  /* Whole row is the affordance into the caller's calls. */
  .cs-list tbody tr.cs-rep__row { cursor: pointer; }
  .cs-rep__go {
    display: inline-flex; align-items: center; justify-content: center;
    width: 26px; height: 26px; border-radius: 8px;
    color: #7386A6; font-size: 15px;
    transition: background-color .15s ease, color .15s ease, transform .15s ease;
  }
  .cs-list tbody tr:hover .cs-rep__go {
    background: rgba(30,111,217,0.10); color: #1E6FD9; transform: translateX(2px);
  }

  /* ---------- narrow screens: stack the table into cards ----------
     Six numeric columns do not fit a phone; below 820px each row becomes a
     labelled block instead of a horizontal scroll nobody discovers. */
  @media (max-width: 820px) {
    .cs-list--stack thead { display: none; }
    .cs-list--stack tbody tr {
      display: block;
      margin: 0 0 10px;
      border: 1px solid rgba(15,37,71,0.10);
      border-radius: 12px;
      padding: 6px 4px;
      background: #fff;
    }
    .cs-list--stack tbody td {
      display: flex; align-items: center; justify-content: space-between;
      gap: 14px;
      border-bottom: none !important;
      padding: 7px 12px !important;
    }
    .cs-list--stack tbody td::before {
      content: attr(data-label);
      font-size: 11px; font-weight: 700; letter-spacing: .05em;
      text-transform: uppercase; color: #52658A;
    }
    .cs-list--stack tbody td:first-child { padding-bottom: 2px !important; }
    .cs-list--stack tbody td:first-child::before { content: none; }
  }

  /* =====================================================================
     Email Setting -- who receives the work-completion report.
     Was two raw Cards side by side listing employee NAMES only, with a red
     Delete button per row: the one screen whose entire job is addresses
     never showed an address.
     ===================================================================== */
  .cs-mail__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 16px;
    align-items: start;
  }
  .cs-mail__panel > * {
    border-radius: 14px !important;
    border: 1px solid var(--cs-line, rgba(15,37,71,0.10)) !important;
    box-shadow: 0 1px 2px rgba(15,37,71,0.04), 0 8px 24px rgba(15,37,71,0.06) !important;
  }
  .cs-mail__hint { margin: -4px 0 14px; font-size: 13px; color: #7386A6; }

  .cs-mail__add {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    align-items: end;
    padding-bottom: 6px;
  }
  .cs-mail__add--stacked { grid-template-columns: 1fr; }
  .cs-mail__add .cs-rep__search { margin-bottom: 4px; min-width: 104px; }

  /* One recipient per row: name, the actual address, and a quiet remove. */
  .cs-mail__list { display: flex; flex-direction: column; gap: 8px; margin-top: 6px; }
  .cs-mail__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: #F7F9FD;
    border: 1px solid rgba(15,37,71,0.07);
    border-radius: 11px;
    transition: border-color .15s ease, background-color .15s ease;
  }
  .cs-mail__row:hover { background: #fff; border-color: rgba(30,111,217,0.24); }
  .cs-mail__avatar {
    flex: 0 0 34px; width: 34px; height: 34px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 10px;
    background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%);
    color: #fff; font-size: 12.5px; font-weight: 700;
  }
  .cs-mail__who { min-width: 0; flex: 1 1 auto; }
  .cs-mail__name {
    margin: 0; font-size: 13.5px; font-weight: 600; color: #0F2547;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cs-mail__addr {
    margin: 1px 0 0; font-size: 12px; color: #52658A;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cs-mail__addr--bad { color: #B45309; font-weight: 600; }
  .cs-mail__flag {
    flex: 0 0 auto;
    padding: 2px 8px; border-radius: 999px;
    background: rgba(245,158,11,0.16); color: #B45309;
    font-size: 10.5px; font-weight: 700; letter-spacing: .04em;
    text-transform: uppercase; white-space: nowrap;
  }
  .cs-mail__remove {
    flex: 0 0 auto;
    width: 30px; height: 30px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1px solid var(--cs-line, rgba(15,37,71,0.10));
    border-radius: 9px;
    background: #fff; color: #7386A6;
    font-size: 16px; cursor: pointer;
    transition: border-color .16s ease, color .16s ease, background-color .16s ease;
  }
  .cs-mail__remove:hover:not([disabled]) {
    border-color: rgba(220,38,38,0.45); color: #DC2626; background: rgba(220,38,38,0.06);
  }
  .cs-mail__remove:focus-visible { outline: 2px solid #1E6FD9; outline-offset: 2px; }
  .cs-mail__remove[disabled] { opacity: .4; cursor: default; }

  /* Confirm in place rather than a modal: the row turns into the question. */
  .cs-mail__row--confirming { background: rgba(220,38,38,0.05); border-color: rgba(220,38,38,0.35); }
  .cs-mail__confirm { display: flex; align-items: center; gap: 8px; flex: 1 1 auto; flex-wrap: wrap; }
  .cs-mail__confirm-text { font-size: 12.5px; color: #991B1B; }
  .cs-mail__confirm .cs-chip { padding: 4px 11px; font-size: 12px; }
  .cs-mail__confirm .cs-chip--danger {
    border-color: rgba(220,38,38,0.45); color: #B91C1C; background: rgba(220,38,38,0.07);
  }
  .cs-mail__confirm .cs-chip--danger:hover { background: rgba(220,38,38,0.14); color: #991B1B; }

  /* Inline edit: the row becomes the form, so you can see the rest of the
     list you are editing against. */
  .cs-mail__row--editing { background: #fff; border-color: rgba(30,111,217,0.35); align-items: flex-end; }
  .cs-mail__edit {
    display: flex; align-items: flex-end; gap: 10px;
    flex: 1 1 auto; flex-wrap: wrap; min-width: 0;
  }
  .cs-mail__edit-current {
    flex: 0 0 auto; align-self: center;
    font-size: 12.5px; color: #52658A; white-space: nowrap;
  }
  .cs-mail__edit-current b { color: #0F2547; font-weight: 600; }
  .cs-mail__edit-field { flex: 1 1 190px; min-width: 150px; }
  .cs-mail__edit-field div { margin-bottom: 0 !important; }
  .cs-mail__edit-field div:empty { display: none; }
  .cs-mail__edit-actions { display: flex; align-items: center; gap: 7px; padding-bottom: 3px; }
  .cs-mail__edit .cs-chip { padding: 6px 13px; }
  .cs-chip--save {
    border-color: transparent;
    background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%);
    color: #fff;
    box-shadow: 0 3px 10px rgba(30,64,175,0.22);
  }
  .cs-chip--save:hover { color: #fff; filter: brightness(1.06);
                         background: linear-gradient(135deg,#0A6BA8 0%,#1E40AF 100%); }
  .cs-chip[disabled] { opacity: .5; cursor: default; }

  .cs-mail__empty {
    padding: 18px 12px; text-align: center;
    font-size: 13px; color: #7386A6;
    background: #F7F9FD; border: 1px dashed rgba(15,37,71,0.14); border-radius: 11px;
  }

  /* The always-on recipient the report hardcodes, shown so the list on screen
     matches the list that actually receives mail. */
  .cs-mail__fixed {
    display: flex; align-items: center; gap: 10px;
    margin-top: 10px; padding: 9px 12px;
    background: rgba(30,111,217,0.05);
    border: 1px dashed rgba(30,111,217,0.28);
    border-radius: 11px;
    font-size: 12.5px; color: #52658A;
  }
  .cs-mail__fixed b { color: #0F2547; font-weight: 600; }

  /* ---------- Q&A detail ---------- */
  .cs-qna { display: flex; flex-direction: column; gap: 18px; }
  .cs-qna__meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
  }
  .cs-qna__meta-item {
    padding: 11px 14px;
    background: #F7F9FD;
    border: 1px solid rgba(15,37,71,0.07);
    border-radius: 11px;
  }
  .cs-qna__meta-label {
    display: block;
    font-size: 10.5px; font-weight: 700; letter-spacing: .06em;
    text-transform: uppercase; color: #52658A;
  }
  .cs-qna__meta-value {
    display: block; margin-top: 3px;
    font-size: 14px; font-weight: 600; color: #0F2547;
    overflow-wrap: anywhere;
  }
  .cs-qna__alert {
    display: flex; align-items: baseline; gap: 8px;
    padding: 11px 14px;
    background: rgba(220,38,38,0.07);
    border: 1px solid rgba(220,38,38,0.30);
    border-radius: 10px;
    font-size: 13px; color: #B91C1C;
  }
  .cs-qna__alert b { color: #991B1B; }

  /* One question per row, answer beneath -- the old layout put both in
     coloured Tags in a 12/12 grid, so a long answer overflowed its pill. */
  .cs-qna__list { display: flex; flex-direction: column; gap: 2px; }
  .cs-qna__item {
    padding: 12px 2px;
    border-bottom: 1px solid rgba(15,37,71,0.07);
  }
  .cs-qna__item:last-child { border-bottom: none; }
  .cs-qna__q {
    margin: 0 0 4px;
    font-size: 12px; font-weight: 700;
    letter-spacing: .03em; color: #52658A;
  }
  .cs-qna__a {
    margin: 0;
    font-size: 14px; color: #0F2547;
    overflow-wrap: anywhere; white-space: pre-wrap;
  }
  .cs-qna__a--empty { color: #9AA8BF; font-style: italic; }

  /* Several responses can share one call; they are shown stacked. */
  .cs-qna__resp-head {
    margin: 0 0 10px;
    font-size: 11.5px; font-weight: 700;
    letter-spacing: .06em; text-transform: uppercase; color: #52658A;
  }
`);
